import { useMemo } from 'react'
import useOfferList from 'hooks/Offers/useOfferList'
import { MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG } from 'constants/charterFlight'
import { OFFER_TYPE_HOTEL } from 'constants/offer'
import Places from 'constants/places'
import config from 'constants/config'

interface Props {
  excludedIds?: Array<string>
}

interface MaldivesCharterOfferList extends App.OfferList {
  bundleFilters?: App.OfferListFilters
}

/**
 * Fetches Maldives charter flight bundle offers with pricing
 * Uses the campaign tag to filter offers that are part of the Maldives Direct program
 * Also fetches bundle pricing (hotel + flight) for the next available charter period
 */
export default function useMaldivesCharterOfferList({ excludedIds }: Props): MaldivesCharterOfferList {
  const defaultRooms = useMemo(() => [config.search.defaultOccupants], [])

  // Filters for fetching offer list (without dates, for general listing)
  const listFilters = useMemo<App.OfferListFilters>(() => ({
    offerTypes: [OFFER_TYPE_HOTEL],
    destinationId: Places.Maldives.id,
    campaigns: [MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG],
    frontPageVisibleOnly: true,
    limit: 25,
  }), [])

  // Bundle filters with dates for pricing calculation
  const bundleFilters = useMemo<App.OfferListFilters>(() => ({
    offerTypes: [OFFER_TYPE_HOTEL],
    destinationId: Places.Maldives.id,
    campaigns: [MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG],
    rooms: defaultRooms,
    isStream: true,
    limit: 25,
  }), [defaultRooms])

  const offerList = useOfferList(listFilters, { excludedIds })

  // Also fetch with bundle filters for pricing (this triggers bundle price calculation)
  useOfferList(bundleFilters, { minRequiredOffers: 1 })

  return {
    ...offerList,
    bundleFilters,
  }
}
